import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Dialog,
  DialogActions,
  Button,
  DialogContent,
  TextField,
  List,
  ListItem,
  ListItemText,
  Fab,
  DialogTitle,
  Paper,
  Link,
  Chip,
  Typography,
  Checkbox,
  IconButton,
  InputAdornment,
  ListItemButton,
  Stack,
} from '@mui/material';
import {
  StaticTreeDataProvider,
  Tree,
  UncontrolledTreeEnvironment,
} from 'react-complex-tree';
import 'react-complex-tree/lib/style-modern.css';

import { useGetCategories, useQueryToFilters } from 'src/entities/category';
import {
  changeCategory,
  createCategory,
  deleteCategory,
} from 'src/entities/category';
import { Add, Close, Delete, Edit } from '@mui/icons-material';
import { Category } from 'src/shared/api';
import { routesMap } from 'src/routes';
import {
  categoriesChangeChildrenOrder,
  categoriesRefresh,
  useGetCategory,
} from 'src/entities/category/api';
import { TypesWithoutCategories } from './TypesWithoutCategories';
import fuzzy from 'fuzzy';
import { WithLoading } from 'src/components/common/WithLoading';
import { WithConfirm } from 'src/shared/ui';

const CategoryFilterType: React.FunctionComponent<{
  type: Type;
  onDelete: any;
}> = ({ type, onDelete }) => {
  return (
    <div key={type?.value}>
      <Chip
        label={
          <Link
            target="_blank"
            href={`${process.env.REACT_APP_PUBLIC_PORTAL_HOST}/products?types=${type?.value}`}
          >
            {type.name}
          </Link>
        }
        variant="outlined"
        onDelete={() => onDelete('types', type?.value)}
      />
    </div>
  );
};

const CategoryFilterAttribute: React.FunctionComponent<{
  attribute: any;
  onDelete: any;
}> = ({ attribute, onDelete }) => {
  return (
    <div>
      {attribute.title}

      {attribute.values.map((value: any) => (
        <div key={value?.value}>
          <Chip
            onDelete={() => onDelete(attribute.key, value?.value)}
            variant="outlined"
            label={
              <Link
                target="_blank"
                href={`${process.env.REACT_APP_PUBLIC_PORTAL_HOST}/products?${attribute.key}=${value?.value}`}
              >
                {value.name}
              </Link>
            }
          />
        </div>
      ))}
    </div>
  );
};

const fuzzyOptions = {
  extract: (el: any) => el.name,
  returnAllOnEmptyString: true,
  returnOriginal: true,
};

const fuzzyFilter = (string: string, data: any, options: any) => {
  if (options?.returnAllOnEmptyString && !string) return data;
  const result = fuzzy.filter(string, data, options);
  if (options?.returnOriginal) {
    return result.map(item => item.original);
  }

  return result;
};

const AddQueryValueDialog: React.FunctionComponent<{
  category?: Category | null;
  filter: any;
  onAdd: any;
  parsedQuery: any;
}> = ({ category, filter, onAdd, parsedQuery }) => {
  const [open, setOpen] = useState(false);
  const [filterQuery, setFilterQuery] = useState('');
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleAdd = (key: string, value: any) => onAdd(key, value);

  const items = filter?.map((item: any) => ({
    ...item,
    values: fuzzyFilter(filterQuery, item.values, fuzzyOptions) || [],
  }));

  return (
    <>
      <Fab color="primary" size="small" onClick={handleOpen}>
        <Add />
      </Fab>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
        <DialogTitle>Добавить фильтр к "{category?.name}"</DialogTitle>

        <DialogContent>
          <Box mt={2}>
            <TextField
              fullWidth
              label="Поиск"
              value={filterQuery}
              onChange={e => setFilterQuery(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      disabled={!filterQuery}
                      onClick={() => setFilterQuery('')}
                    >
                      <Close />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          {items?.map((item: any) => (
            <Box key={item.key} mt={2}>
              <Box>
                <Typography variant="h5">{item.title}</Typography>
              </Box>
              <List>
                {item.values?.map((value: any) => (
                  <ListItem
                    secondaryAction={
                      <Checkbox
                        onClick={() => handleAdd(item.key, value.value)}
                        checked={parsedQuery[item.key]?.includes(
                          String(value.value),
                        )}
                      />
                    }
                    key={value.value}
                    divider
                  >
                    <ListItemButton
                      selected={parsedQuery[item.key]?.includes(
                        String(value.value),
                      )}
                      onClick={() => handleAdd(item.key, value.value)}
                    >
                      <ListItemText primary={value.name} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Отмена</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const EditCategoryDialog: React.FunctionComponent<{
  parent?: Category | null;
  category?: Category | null;
  afterCreate: () => any;
  open: boolean;
  onClose: () => any;
}> = ({ afterCreate, parent, category: providedCategory, open, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [query, setQuery] = useState<null | string>(null);

  const categoryRequest = useGetCategory({
    variables: { id: providedCategory?.id as number },
    skip: !providedCategory || !open,
  });

  const category = categoryRequest.data;

  const queryFilters = useQueryToFilters({
    query: category?.query,
    skip: !open,
  });

  useEffect(() => {
    if (category) {
      setName(category.name || '');
      setQuery(category.query || null);
      setDescription(category.description || '');
    }
  }, [category, open]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const reset = () => {
    setName('');
    setQuery(null);
    setLoading(false);
  };

  const handleSave = () => {
    category ? handleChange() : hanldeCreate();
  };

  const handleChange = () => {
    if (!category?.id) return null;
    const nextCategory: {
      name?: string;
      query?: string | null;
      description?: string | null;
    } = {};

    if (category?.name !== name && name) {
      nextCategory.name = name;
    }

    if (category?.query !== query) {
      nextCategory.query = query || null;
    }

    if (category?.description !== description) {
      nextCategory.description = description || null;
    }

    setLoading(true);

    handleChangeRequest(nextCategory);
  };

  const handleChangeRequest = (nextCategory: any) => {
    if (!category?.id) return null;
    changeCategory(category.id, nextCategory).then(() => {
      reset();
      afterCreate();
      onClose();
      categoryRequest.mutate();
    });
  };

  const hanldeCreate = () => {
    const nextCategory: { name: string; parent?: number } = { name };
    if (parent) {
      nextCategory.parent = parent.id;
    }

    setLoading(true);

    createCategory(nextCategory).then(() => {
      reset();
      afterCreate();
      onClose();
    });
  };

  const handleDeleteQueryItem = (key: any, value: any) => {
    if (!category?.id) return null;
    const nextQuery = {
      ...queryFilters.parsedQuery,
      //@ts-ignore
      [key]: queryFilters.parsedQuery[key].filter(
        //@ts-ignore
        queryValue => queryValue !== String(value),
      ),
    };

    const nextQueryString = new URLSearchParams(
      Object.entries(nextQuery).reduce(
        (prev: any, curr: any) =>
          curr[1]?.length ? { ...prev, [curr[0]]: curr[1] } : prev,
        {},
      ),
    ).toString();

    changeCategory(category.id, { query: nextQueryString }).then(() => {
      categoryRequest.mutate();
      afterCreate();
    });
  };

  const handleAddQueryItem = (key: any, value: any) => {
    if (!category?.id) return null;

    //@ts-ignore
    if (queryFilters?.parsedQuery?.[key]?.includes(String(value))) {
      return handleDeleteQueryItem(key, value);
    }

    const nextQuery = {
      ...queryFilters.parsedQuery,
      //@ts-ignore
      [key]: Array.from(
        //@ts-ignore
        new Set(queryFilters.parsedQuery[key] || []).add(value),
      ),
    };

    const nextQueryString = new URLSearchParams(nextQuery).toString();

    changeCategory(category.id, { query: nextQueryString }).then(() => {
      categoryRequest.mutate();
      afterCreate();
    });
  };

  const disabled = loading;

  return (
    <>
      {/* {parent && (
        <Fab color="primary" size="small" onClick={handleOpen}>
          <Add />
        </Fab>
      )}
      {!parent && !providedCategory && (
        <Button onClick={handleOpen} variant="contained" color="primary">
          Новая категория
        </Button>
      )}
      {providedCategory && (
        <Fab color="primary" size="small" onClick={handleOpen}>
          <Edit />
        </Fab>
      )} */}
      {open && (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
          {category && <DialogTitle>Изменить "{category.name}"</DialogTitle>}
          {parent && (
            <DialogTitle>Добавить подкатегорию к "{parent.name}"</DialogTitle>
          )}
          <DialogContent>
            <Box mt={2}>
              <TextField
                label="Название"
                value={name}
                onChange={e => setName(e.target.value)}
                fullWidth
              />
            </Box>
            <Box mt={2}>
              <TextField
                disabled={Boolean(category?.children?.length)}
                value={query}
                label="Query"
                onChange={e => setQuery(e.target.value)}
                fullWidth
              />
            </Box>
            {Boolean(queryFilters.queryTypes?.length) && (
              <Box mt={2}>
                <Box display="flex" alignItems="center">
                  <Typography variant="h5">Типы товаров</Typography>
                  <Box ml={2}>
                    <AddQueryValueDialog
                      parsedQuery={queryFilters.parsedQuery}
                      onAdd={handleAddQueryItem}
                      category={category}
                      filter={[
                        {
                          key: 'types',
                          title: 'Тип товара',
                          values: queryFilters.types,
                        },
                      ]}
                    />
                  </Box>
                </Box>
                {queryFilters.queryTypes?.map((type: Type) => (
                  <CategoryFilterType
                    type={type}
                    key={type.value}
                    onDelete={handleDeleteQueryItem}
                  />
                ))}
              </Box>
            )}
            {Boolean(queryFilters.queryTypes?.length) && (
              <Box mt={2}>
                <Box display="flex" alignItems="center">
                  <Typography variant="h5">Аттрибуты</Typography>
                  <Box ml={2}>
                    <AddQueryValueDialog
                      parsedQuery={queryFilters.parsedQuery}
                      onAdd={handleAddQueryItem}
                      category={category}
                      filter={queryFilters.attributes}
                    />
                  </Box>
                </Box>
                {queryFilters.queryAttributes?.map((attribute: any) => (
                  <CategoryFilterAttribute
                    attribute={attribute}
                    key={attribute.value}
                    onDelete={handleDeleteQueryItem}
                  />
                ))}
              </Box>
            )}
            <Box mt={2}>
              <TextField
                label="Описание"
                value={description}
                onChange={e => setDescription(e.target.value)}
                fullWidth
                multiline
                minRows={3}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Отмена</Button>
            <Button
              variant="contained"
              color="primary"
              disabled={disabled}
              onClick={handleSave}
            >
              {category ? 'Сохранить' : 'Создать'}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

interface Type {
  count: number;
  name: string;
  value: number;
}

const CategoryListItem: React.FunctionComponent<{
  category: Category;
  afterCreate: () => any;
  afterDelete: () => any;
  level: number;
  actions: ReactNode;
}> = ({ category, afterCreate, level, afterDelete, actions }) => {
  return (
    <ListItem secondaryAction={actions}>
      <ListItemText
        primary={
          <Link
            target="_blank"
            href={`${process.env.REACT_APP_PUBLIC_PORTAL_HOST}/categories/${category.id}`}
          >
            {category.name}
          </Link>
        }
        secondary={
          category.query && (
            <Box>
              <Box>
                <Link
                  target="_blank"
                  href={`${process.env.REACT_APP_PUBLIC_PORTAL_HOST}/products?${category.query}`}
                >
                  <span style={{ wordBreak: 'break-all' }}>
                    {category.query}
                  </span>
                </Link>
              </Box>
            </Box>
          )
        }
      />
    </ListItem>
  );
};

const CategoriesList: React.FunctionComponent<{
  categories?: Category[];
  afterCreate: () => any;
  afterDelete: () => any;
  level: number;
}> = ({ categories, afterCreate, level, afterDelete }) => {
  const [selectedCategory, setCategory] = useState<null | Category>(null);
  const [categoryForAddCategory, setForAddCategory] = useState<null | Category>(
    null,
  );

  const onDelete = (category: Category) =>
    deleteCategory(category.id as number).then(() => {
      afterDelete();
    });

  const getActions = (category: Category) => (
    <Stack direction="row" spacing={1}>
      {level < 3 && (
        <Fab
          color="primary"
          size="small"
          onClick={e => {
            e.stopPropagation();
            setForAddCategory(category);
          }}
        >
          <Add />
        </Fab>
      )}
      {!category.children?.length && (
        <WithConfirm onConfirm={() => onDelete(category)}>
          <IconButton color="error" size="small">
            <Delete />
          </IconButton>
        </WithConfirm>
      )}
      <IconButton
        size="small"
        onClick={e => {
          e.stopPropagation();
          setCategory(category);
        }}
      >
        <Edit />
      </IconButton>
    </Stack>
  );

  const categoryToDraggable = (category: Category, index: string | number) => ({
    index,
    isFolder: Boolean(category.children?.length),
    children: (category.children || []).map(item => item.id) as number[],
    data: (
      <CategoryListItem
        category={category}
        afterCreate={afterCreate}
        afterDelete={afterDelete}
        level={level}
        actions={getActions(category)}
      />
    ),
    name: category.name,
    canMove: true,
  });

  //@ts-ignore
  const childrenToDraggable = (categories?: Category[] = []) =>
    categories.reduce(
      //@ts-ignore
      (prev, curr) => ({
        ...prev,
        [curr.id as number]: categoryToDraggable(curr, curr.id as number),
        ...childrenToDraggable(curr.children),
      }),
      {},
    );

  const dataProvider = useMemo(
    () =>
      new StaticTreeDataProvider<Category>(
        //@ts-ignore
        {
          root: categoryToDraggable(
            { name: 'root', children: categories },
            'root',
          ),
          ...childrenToDraggable(categories),
        },
      ),
    [categories],
  );

  const handleChangeOrder = (index: any) => {
    dataProvider.getTreeItem(index).then((item: any) => {
      categoriesChangeChildrenOrder(
        item.index === 'root' ? 0 : item.index,
        item.children.map((id: number) => ({ id })),
      ).then(() => afterDelete());
    });
  };

  dataProvider?.onDidChangeTreeData(handleChangeOrder);

  if (!categories) return null;

  return (
    <>
      <EditCategoryDialog
        parent={selectedCategory ? null : categoryForAddCategory}
        afterCreate={afterCreate}
        category={categoryForAddCategory ? null : selectedCategory}
        open={Boolean(selectedCategory || categoryForAddCategory)}
        onClose={() => {
          setCategory(null);
          setForAddCategory(null);
        }}
      />
      <style>{`
        :root {
          --rct-color-tree-bg: unset;
          --rct-color-tree-focus-outline: unset;

          --rct-color-focustree-item-selected-bg: unset;
          --rct-color-focustree-item-selected-text: inherit;
          --rct-color-focustree-item-focused-border: #0366d6;
          --rct-color-focustree-item-draggingover-bg: rgba(3, 102, 214, 0.5);
          --rct-color-focustree-item-draggingover-color: inherit;
          --rct-color-focustree-item-hover-bg: unset;
          --rct-color-focustree-item-active-bg: unset;

          --rct-color-nonfocustree-item-selected-bg: unset;
          --rct-color-nonfocustree-item-selected-text: inherit;
          --rct-color-nonfocustree-item-focused-border: #dbdbdb;

          --rct-color-search-highlight-bg: unset;
          --rct-color-drag-between-line-bg: #0366d6;
          --rct-color-arrow: #373a3f;

          --rct-item-height: 60px;

          --rct-color-renaming-input-submitbutton-bg: inherit;
          --rct-color-renaming-input-submitbutton-bg-hover: #0366d6;
          --rct-color-renaming-input-submitbutton-bg-active: #095fc1;

          --rct-color-renaming-input-submitbutton-text: inherit;
          --rct-color-renaming-input-submitbutton-text-hover: #ffffff;
          --rct-color-renaming-input-submitbutton-text-active: #ffffff;
        }
      `}</style>
      <UncontrolledTreeEnvironment
        dataProvider={dataProvider}
        getItemTitle={item => item.data}
        viewState={{}}
        canDragAndDrop={true}
        canReorderItems={true}
        canDropOnFolder={true}
        canDropOnNonFolder={true}
        canSearch={false}
      >
        <Tree treeId="tree-5" rootItem="root" treeLabel="Tree Example" />
      </UncontrolledTreeEnvironment>
    </>
  );
};

interface Props {}

export const CategoriesPage: React.FunctionComponent<Props> = () => {
  const [loadingRefresh, setLoadingRefresh] = useState(false);
  const [open, setOpen] = useState(false);

  const categoriesRequest = useGetCategories({
    variables: { withEmpty: true },
  });

  const handleRefreshTCategoriesTree = () => {
    setLoadingRefresh(true);
    categoriesRefresh().then(() => {
      setLoadingRefresh(false);
      categoriesRequest.mutate();
    });
  };

  return (
    <Box>
      <Box display="flex" alignItems="center">
        <Fab onClick={() => setOpen(true)} color="primary">
          <Add />
        </Fab>
        <EditCategoryDialog
          open={open}
          afterCreate={categoriesRequest.mutate}
          onClose={() => setOpen(false)}
        />
        <Box ml={2}>
          <Link
            target="_blank"
            href={routesMap.categoriesMissingProducts.getRoute()}
          >
            Товары без категорий
          </Link>
        </Box>
        <Box ml={2}>
          <WithLoading loading={loadingRefresh}>
            <Button
              variant="outlined"
              disabled={loadingRefresh}
              onClick={handleRefreshTCategoriesTree}
            >
              Обновить дерево
            </Button>
          </WithLoading>
        </Box>
      </Box>
      <Box mt={4}>
        <TypesWithoutCategories categories={categoriesRequest.data} />
      </Box>
      <Box mt={4} maxWidth="800px">
        <Paper>
          <Box p={2}>
            <CategoriesList
              categories={categoriesRequest.data}
              afterCreate={categoriesRequest.mutate}
              afterDelete={categoriesRequest.mutate}
              level={1}
            />
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};
